import { Grid, IconButton, Tooltip } from '@mui/material';
import { Cancel as CancelIcon, Search as SearchIcon } from '@tassat/icon';
import * as React from 'react';

import muiStyles from './styles';
import { SearchFieldProps } from './types';

export default function SearchField(props: SearchFieldProps) {
    const {
        id,
        iconColor,
        searchValue,
        onSearch,
        toggleSearchBar,
        isOpen
    } = props;

    const inputRef = React.useRef<HTMLInputElement>(null);
    const [ inputDisplayed, setInputDisplayed ] = React.useState<boolean>(false);
    const classes = muiStyles(props);

    const handleChange = (e) => {
        onSearch(e.target.value);
    };

    const closeSearch = React.useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape' || e.key === 'Enter') {
                toggleSearchBar(false);
            }
        },
        [toggleSearchBar]
    );

    const clearSearch = (e) => {
        e.preventDefault();
        e.stopPropagation();
        onSearch('');
    };

    // eslint-disable-next-line consistent-return
    React.useEffect(() => {
        setInputDisplayed(isOpen);

        if (isOpen) {
            window.addEventListener('keydown', closeSearch);

            return () => window.removeEventListener('keydown', closeSearch);
        }
    }, [ isOpen, closeSearch ]);

    React.useEffect(() => {
        if (inputDisplayed && inputRef.current) {
            inputRef.current.focus();
        }
    }, [inputDisplayed]);

    const searchContainerClass = React.useMemo(() => {
        if (!isOpen) {
            return searchValue ?
                classes.active
                : classes.buttonClosed;
        }

        return '';
    }, [ isOpen, searchValue, classes.active, classes.buttonClosed ]);

    return (
        <Grid
            className={`${classes.root} ${searchContainerClass}`}
            container
        >
            <input
                className={`${classes.searchInput} ${isOpen ? classes.searchBarOpen : classes.searchBarClosed}`}
                onBlur={() => toggleSearchBar(false)}
                onChange={handleChange}
                placeholder='Search...'
                ref={inputRef}
                type='text'
                value={searchValue}
            />
            {isOpen &&
                (
                <button
                    onMouseDown={(e) => clearSearch(e)}
                    title='Clear search'
                    type='button'
                >
                    <CancelIcon
                        className={classes.cancel}
                    />
                </button>
            )}
            <Tooltip
                classes={{ popper: classes.tooltip }}
                disableInteractive
                title='Toggle search'
            >
                <IconButton
                    className={`${classes.icon}`}
                    data-testid='icon-button'
                    disableFocusRipple
                    id={id}
                    onMouseDown={() => !isOpen && toggleSearchBar(true)}
                >
                    <SearchIcon
                        className={classes.searchIcon}
                        iconColor={iconColor}
                    />
                </IconButton>
            </Tooltip>
        </Grid>
    );
}